import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Heart, List, Map as MapIcon, MapPin, Navigation, Clock, ListOrdered, Car, Loader2, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { api, errMsg, fmtINR, fmtDateTime } from "../../../lib/api";
import { pokeLive } from "../../../lib/useLive";
import { AvailabilityBars, VT_META, VT_KEYS, fmtHours, fmtMinutes, occupancyTone } from "../../../components/parking/common";
import { LocationMap } from "../../../components/parking/LocationMap";
import { PayModePicker } from "../../../components/parking/PayFlow";

/* Phase 201 — Find & Book: live list / map of locations, favourites, distance (when location is allowed), booking
   dialog with instant quote, saved vehicles, waitlist when full. */

const DURATIONS = [30, 60, 120, 180, 240];

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function FindTab({ ctx }) {
  const { code, live, summary, startPay, showTicket, refresh, walletBalance } = ctx;
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [view, setView] = useState("list");
  const [vt, setVt] = useState("four_wheeler");
  const [q, setQ] = useState("");
  const [booking, setBooking] = useState(null);     // location being booked
  const [waitlistFor, setWaitlistFor] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [manageVehicles, setManageVehicles] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}, { timeout: 6000, maximumAge: 300000 });
  }, []);

  const load = useCallback(() => {
    const p = geo ? `?lat=${geo.lat}&lng=${geo.lng}` : "";
    return api.get(`/t/${code}/parking/citizen/locations${p}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [code, geo]);
  useEffect(() => { load(); }, [load, live]);
  const loadVehicles = useCallback(() => api.get(`/t/${code}/parking/citizen/vehicles`).then((r) => setVehicles(r.data)).catch(() => {}), [code]);
  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const toggleFav = async (l) => {
    try {
      if (l.is_favourite) await api.delete(`/t/${code}/parking/citizen/favourites/${l.id}`);
      else await api.post(`/t/${code}/parking/citizen/favourites/${l.id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rows = useMemo(() => {
    const list = data?.locations || [];
    const s = q.trim().toLowerCase();
    return s ? list.filter((l) => `${l.name} ${l.address || ""} ${l.code || ""}`.toLowerCase().includes(s)) : list;
  }, [data, q]);

  const tariff = data?.tariffs?.[vt];
  const notified = (summary?.waitlist || []).filter((w) => w.status === "notified");

  if (!data) return <Spinner label="Finding parking near you…" />;

  return (
    <div className="space-y-4" data-testid="citizen-parking-find">
      {notified.map((w) => (
        <Card key={w.id} className="border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 shadow-sm" data-testid={`waitlist-notified-${w.id}`}>
          <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
            <div><p className="font-medium text-sm">A {VT_META[w.vehicle_type]?.label || w.vehicle_type} slot is free at {w.location_name}</p><p className="text-xs text-muted-foreground">Book before {fmtDateTime(w.hold_until)} to keep it.</p></div>
            <Button size="sm" onClick={() => { setVt(w.vehicle_type); setBooking(data.locations.find((l) => l.id === w.location_id) || null); }} data-testid={`waitlist-book-now-${w.id}`}>Book now</Button>
          </CardContent>
        </Card>
      ))}

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex gap-1 rounded-lg border p-1 bg-card w-full sm:w-auto" role="tablist" aria-label="Vehicle class" data-testid="find-vehicle-type">
          {VT_KEYS.map((k) => {
            const m = VT_META[k];
            return (
              <button key={k} type="button" onClick={() => setVt(k)} data-testid={`find-vt-${k}`}
                className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm min-h-[40px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${vt === k ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
                <m.icon className="h-4 w-4" /> {m.short}
              </button>
            );
          })}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or area" className="h-10 sm:max-w-xs" data-testid="find-search" />
        <div className="flex items-center gap-2 sm:ml-auto">
          {geo ? <Badge variant="outline" className="gap-1 text-[11px]"><Navigation className="h-3 w-3" /> Sorted by distance</Badge> : null}
          <div className="flex gap-1 rounded-lg border p-1 bg-card">
            <Button type="button" size="sm" variant={view === "list" ? "default" : "ghost"} className="h-8" onClick={() => setView("list")} data-testid="find-view-list"><List className="h-4 w-4 mr-1" /> List</Button>
            <Button type="button" size="sm" variant={view === "map" ? "default" : "ghost"} className="h-8" onClick={() => setView("map")} data-testid="find-view-map"><MapIcon className="h-4 w-4 mr-1" /> Map</Button>
          </div>
        </div>
      </div>

      {tariff && (
        <p className="text-xs text-muted-foreground" data-testid="find-tariff-hint">
          {VT_META[vt].label}: <span className="font-medium text-foreground">{fmtINR(tariff.first_hour)}</span> first hour, then <span className="font-medium text-foreground">{fmtINR(tariff.subsequent_hour)}</span> per hour · Day pass {fmtINR(tariff.day_pass)} · Monthly {fmtINR(tariff.monthly_pass)}
        </p>
      )}

      {view === "map" && (
        <LocationMap locations={rows} height={360} onSelect={(l) => setBooking(l)}
          renderPopup={(l) => (
            <div className="space-y-1 min-w-[180px]">
              <p className="font-semibold text-sm">{l.name}</p>
              <p className="text-xs text-muted-foreground">{fmtHours(l.hours_today)}</p>
              <p className="text-xs">{l.occupancy?.available?.[vt] ?? 0} {VT_META[vt].short} free</p>
              <Button size="sm" className="h-8 w-full" onClick={() => setBooking(l)}>Book</Button>
            </div>
          )} />
      )}

      {rows.length === 0 ? <EmptyState title="No parking locations match" subtitle="Try another name or clear the search." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="find-location-list">
          {rows.map((l) => {
            const avail = l.occupancy?.available?.[vt] ?? 0;
            const cap = l.occupancy?.capacity?.[vt] ?? 0;
            const pct = l.occupancy?.total?.occupancy_pct ?? 0;
            const full = cap > 0 && avail === 0;
            return (
              <Card key={l.id} className="shadow-sm hover:shadow-md transition-shadow" data-testid={`location-card-${l.code || l.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold font-heading leading-tight flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary shrink-0" /> <span className="truncate">{l.name}</span></p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{l.address || l.code}</p>
                    </div>
                    <button type="button" onClick={() => toggleFav(l)} aria-label={l.is_favourite ? "Remove favourite" : "Add favourite"} data-testid={`location-fav-${l.code || l.id}`}
                      className="h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0">
                      <Heart className={`h-4 w-4 ${l.is_favourite ? "fill-destructive text-destructive" : "text-muted-foreground"}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {l.open_now ? <Badge variant="outline" className="border-[hsl(var(--success))] text-[hsl(var(--success))]">Open</Badge> : <Badge variant="secondary">Closed</Badge>}
                    <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtHours(l.hours_today)}</span>
                    {l.distance_km != null && <span className="text-muted-foreground inline-flex items-center gap-1"><Navigation className="h-3 w-3" /> {l.distance_km} km</span>}
                    <span className={`ml-auto font-semibold tabular-nums ${occupancyTone(pct)}`}>{pct}% full</span>
                  </div>
                  <AvailabilityBars occupancy={l.occupancy} testId={`location-avail-${l.code || l.id}`} />
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <p className="text-sm" data-testid={`location-free-${l.code || l.id}`}>
                      <span className={`font-semibold ${full ? "text-destructive" : ""}`}>{avail}</span> <span className="text-muted-foreground">{VT_META[vt].short} slot{avail === 1 ? "" : "s"} free{cap ? ` of ${cap}` : ""}</span>
                    </p>
                    {full && l.waitlist_enabled ? (
                      <Button size="sm" variant="outline" className="h-10" onClick={() => setWaitlistFor(l)} data-testid={`location-waitlist-${l.code || l.id}`}><ListOrdered className="h-4 w-4 mr-1.5" /> Join waitlist</Button>
                    ) : (
                      <Button size="sm" className="h-10" disabled={cap === 0} onClick={() => setBooking(l)} data-testid={`location-book-${l.code || l.id}`}>Book {VT_META[vt].short}</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Car className="h-4 w-4 text-primary" /> My vehicles</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setManageVehicles(true)} data-testid="manage-vehicles-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </CardHeader>
        <CardContent>
          {vehicles.length === 0 ? <p className="text-sm text-muted-foreground">Save your vehicle numbers once — booking takes one tap after that.</p> : (
            <div className="flex flex-wrap gap-2" data-testid="my-vehicles">
              {vehicles.map((v) => {
                const m = VT_META[v.vehicle_type] || VT_META.four_wheeler;
                return <Badge key={v.id} variant="outline" className="gap-1.5 py-1.5 px-2.5 font-mono text-xs" data-testid={`vehicle-chip-${v.vehicle_number}`}><m.icon className="h-3.5 w-3.5" /> {v.vehicle_number}{v.nickname ? <span className="font-sans text-muted-foreground">· {v.nickname}</span> : null}</Badge>;
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {booking && (
        <BookingDialog code={code} loc={booking} data={data} vt={vt} vehicles={vehicles} walletBalance={walletBalance}
          onClose={() => setBooking(null)}
          onBooked={({ booking: b, payment }) => {
            setBooking(null);
            loadVehicles();
            startPay(payment, {
              title: "Pay for parking",
              lines: [{ label: "Booking", value: b.booking_no }, { label: "Location", value: b.location_name }, { label: "Vehicle", value: b.vehicle_number }, { label: "Time", value: `${fmtDateTime(b.start_at)} → ${fmtDateTime(b.end_at)}` }],
              onPaid: (res) => { toast.success(`Parking booked — ${b.booking_no}`); refresh(); showTicket({ booking: res?.ref || b, title: "You're booked!", description: "Show this QR to the marshal at the gate." }); },
            });
            if (payment?.status === "paid") { toast.success(`Parking booked — ${b.booking_no} (paid from wallet)`); refresh(); showTicket({ booking: payment.ref || b, title: "You're booked!", description: "Paid from wallet. Show this QR to the marshal at the gate." }); }
          }} />
      )}
      {waitlistFor && <WaitlistDialog code={code} loc={waitlistFor} vt={vt} vehicles={vehicles} onClose={() => setWaitlistFor(null)} onJoined={() => { setWaitlistFor(null); refresh(); pokeLive(code, "citizen"); }} />}
      {manageVehicles && <VehiclesDialog code={code} vehicles={vehicles} onClose={() => setManageVehicles(false)} onChanged={loadVehicles} />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------
export function BookingDialog({ code, loc, data, vt: initialVt, vehicles, walletBalance, onClose, onBooked }) {
  const settings = data?.settings || {};
  const [vt, setVt] = useState(initialVt || "four_wheeler");
  const [vehicleNumber, setVehicleNumber] = useState(vehicles?.[0]?.vehicle_number || "");
  const [saveVehicle, setSaveVehicle] = useState(false);
  const [when, setWhen] = useState("now");
  const [startAt, setStartAt] = useState(() => toLocalInput(new Date(Date.now() + 30 * 60000)));
  const [duration, setDuration] = useState(60);
  const [custom, setCustom] = useState("");
  const [mode, setMode] = useState((settings.payment_modes_citizen || ["upi"])[0] || "upi");
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const minM = Number(settings.min_duration_minutes || 30), maxM = Number(settings.max_duration_hours || 12) * 60;
  const maxStart = toLocalInput(new Date(Date.now() + Number(settings.advance_booking_hours || 2) * 3600000));
  const startIso = when === "now" ? null : (startAt ? new Date(startAt).toISOString() : null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setQuoteErr("");
      api.post(`/t/${code}/parking/citizen/quote`, { location_id: loc.id, vehicle_type: vt, start_at: startIso, duration_minutes: duration })
        .then((r) => setQuote(r.data))
        .catch((e) => { setQuote(null); setQuoteErr(errMsg(e)); });
    }, 350);
    return () => clearTimeout(timer.current);
  }, [code, loc.id, vt, startIso, duration]);

  // pick the first saved vehicle of the chosen class when the class changes
  useEffect(() => {
    const v = (vehicles || []).find((x) => x.vehicle_type === vt);
    if (v) setVehicleNumber(v.vehicle_number);
  }, [vt, vehicles]);

  const submit = async () => {
    if (!vehicleNumber.trim()) { toast.error("Enter your vehicle number"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/parking/citizen/bookings`, { location_id: loc.id, vehicle_type: vt, start_at: startIso, duration_minutes: duration, vehicle_number: vehicleNumber.trim().toUpperCase(), payment_mode: mode, save_vehicle: saveVehicle });
      onBooked(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const availNow = loc.occupancy?.available?.[vt] ?? 0;
  const canBook = quote && quote.available > 0 && !busy && (mode !== "wallet" || Number(walletBalance) >= Number(quote.amount));

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {loc.name}</DialogTitle>
          <DialogDescription>{fmtHours(loc.hours_today)} · {loc.address || loc.code}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Vehicle class</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="booking-vehicle-type">
              {VT_KEYS.map((k) => {
                const m = VT_META[k]; const a = loc.occupancy?.available?.[k] ?? 0; const c = loc.occupancy?.capacity?.[k] ?? 0;
                return (
                  <button key={k} type="button" disabled={c === 0} onClick={() => setVt(k)} data-testid={`booking-vt-${k}`}
                    className={`rounded-lg border p-2.5 text-left min-h-[56px] transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${vt === k ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-accent/60"}`}>
                    <span className="flex items-center gap-1.5 text-sm font-medium"><m.icon className="h-4 w-4" /> {m.short}</span>
                    <span className={`block text-[11px] ${a === 0 ? "text-destructive" : "text-muted-foreground"}`}>{a} free</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label htmlFor="booking-vehicle-number" className="text-xs">Vehicle number</Label>
            <Input id="booking-vehicle-number" value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())} placeholder="e.g. OD02AB1234" className="h-11 font-mono uppercase mt-1" data-testid="booking-vehicle-number" />
            {vehicles?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {vehicles.map((v) => <button key={v.id} type="button" onClick={() => { setVehicleNumber(v.vehicle_number); if (VT_KEYS.includes(v.vehicle_type)) setVt(v.vehicle_type); }} data-testid={`booking-saved-${v.vehicle_number}`}
                  className={`rounded-full border px-2.5 py-1 text-xs font-mono hover:bg-accent ${vehicleNumber === v.vehicle_number ? "border-primary bg-primary/5" : ""}`}>{v.vehicle_number}</button>)}
              </div>
            )}
            {!vehicles?.some((v) => v.vehicle_number === vehicleNumber.replace(/[^A-Z0-9]/g, "")) && (
              <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer"><Checkbox checked={saveVehicle} onCheckedChange={(c) => setSaveVehicle(!!c)} data-testid="booking-save-vehicle" /> Save this vehicle for next time</label>
            )}
          </div>
          <div>
            <Label className="text-xs">Arrival</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button" onClick={() => setWhen("now")} data-testid="booking-when-now" className={`rounded-lg border px-3 py-2.5 text-sm min-h-[44px] ${when === "now" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-accent/60"}`}>Arriving now</button>
              <button type="button" onClick={() => setWhen("later")} data-testid="booking-when-later" className={`rounded-lg border px-3 py-2.5 text-sm min-h-[44px] ${when === "later" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-accent/60"}`}>Later (up to {settings.advance_booking_hours || 2} h ahead)</button>
            </div>
            {when === "later" && <Input type="datetime-local" value={startAt} min={toLocalInput(new Date())} max={maxStart} onChange={(e) => setStartAt(e.target.value)} className="h-11 mt-2" data-testid="booking-start-at" />}
          </div>
          <div>
            <Label className="text-xs">Duration</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="booking-duration">
              {DURATIONS.filter((d) => d >= minM && d <= maxM).map((d) => (
                <button key={d} type="button" onClick={() => { setDuration(d); setCustom(""); }} data-testid={`booking-duration-${d}`}
                  className={`rounded-full border px-3 py-2 text-sm min-h-[40px] ${duration === d && !custom ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{fmtMinutes(d)}</button>
              ))}
              <Input type="number" min={minM} max={maxM} step={15} value={custom} placeholder="Custom min" onChange={(e) => { setCustom(e.target.value); const n = Number(e.target.value); if (n >= minM && n <= maxM) setDuration(n); }} className="h-10 w-[120px]" data-testid="booking-duration-custom" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Between {fmtMinutes(minM)} and {fmtMinutes(maxM)} · charged per started hour</p>
          </div>
          <div className="rounded-lg bg-accent/50 p-3" data-testid="booking-quote">
            {quoteErr ? <p className="text-sm text-destructive" data-testid="booking-quote-error">{quoteErr}</p> : !quote ? <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculating fare…</p> : (
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  <p>{fmtDateTime(quote.start_at)} → {fmtDateTime(quote.end_at)}</p>
                  <p>{fmtINR(quote.tariff.first_hour)} first hour + {fmtINR(quote.tariff.subsequent_hour)} × further hour(s)</p>
                  <p className={quote.available > 0 ? "text-[hsl(var(--success))] font-medium" : "text-destructive font-medium"} data-testid="booking-quote-availability">{quote.available > 0 ? `${quote.available} ${VT_META[vt].short} slot(s) free for this time` : "No slot free for this time"}</p>
                </div>
                <p className="text-2xl font-semibold tabular-nums font-heading" data-testid="booking-quote-amount">{fmtINR(quote.amount)}</p>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Pay with</Label>
            <div className="mt-1"><PayModePicker modes={settings.payment_modes_citizen} value={mode} onChange={setMode} walletBalance={walletBalance} fastagAvailable={data?.fastag_available} amount={quote?.amount} testId="booking-pay-mode" /></div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0 sticky bottom-0 bg-background pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="booking-cancel">Cancel</Button>
          {quote && quote.available === 0 && loc.waitlist_enabled && availNow === 0 ? (
            <Button variant="secondary" className="h-11" onClick={async () => { try { await api.post(`/t/${code}/parking/citizen/waitlist`, { location_id: loc.id, vehicle_type: vt, vehicle_number: vehicleNumber || "TEMP0000" }); toast.success("Added to the waitlist — we will alert you when a slot frees up"); pokeLive(code, "citizen"); onClose(); } catch (e) { toast.error(errMsg(e)); } }} data-testid="booking-join-waitlist"><ListOrdered className="h-4 w-4 mr-1.5" /> Join waitlist</Button>
          ) : (
            <Button className="h-11" onClick={submit} disabled={!canBook} data-testid="booking-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Book & pay {quote ? fmtINR(quote.amount) : ""}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------
function WaitlistDialog({ code, loc, vt, vehicles, onClose, onJoined }) {
  const [vehicleNumber, setVehicleNumber] = useState(vehicles?.find((v) => v.vehicle_type === vt)?.vehicle_number || vehicles?.[0]?.vehicle_number || "");
  const [busy, setBusy] = useState(false);
  const join = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/parking/citizen/waitlist`, { location_id: loc.id, vehicle_type: vt, vehicle_number: vehicleNumber });
      toast.success(`You are #${r.data.position || 1} on the waitlist at ${loc.name} — we will alert you when a ${VT_META[vt].short} slot frees up`);
      onJoined(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="waitlist-dialog">
        <DialogHeader><DialogTitle className="font-heading">Join the waitlist — {loc.name}</DialogTitle><DialogDescription>All {VT_META[vt].label} slots are taken. You get an alert (in-app + SMS when configured) the moment one frees up, with a short window to book it.</DialogDescription></DialogHeader>
        <div><Label htmlFor="waitlist-vehicle" className="text-xs">Vehicle number</Label><Input id="waitlist-vehicle" value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())} placeholder="e.g. OD02AB1234" className="h-11 font-mono mt-1" data-testid="waitlist-vehicle-number" /></div>
        <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button><Button className="h-11" onClick={join} disabled={busy || vehicleNumber.trim().length < 4} data-testid="waitlist-join-button"><ListOrdered className="h-4 w-4 mr-1.5" /> Join waitlist</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------
function VehiclesDialog({ code, vehicles, onClose, onChanged }) {
  const [vn, setVn] = useState("");
  const [vt, setVt] = useState("four_wheeler");
  const [nick, setNick] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try { await api.post(`/t/${code}/parking/citizen/vehicles`, { vehicle_number: vn, vehicle_type: vt, nickname: nick || null }); setVn(""); setNick(""); toast.success("Vehicle saved"); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (v) => { try { await api.delete(`/t/${code}/parking/citizen/vehicles/${v.id}`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="vehicles-dialog">
        <DialogHeader><DialogTitle className="font-heading">My vehicles</DialogTitle><DialogDescription>Up to 10 saved vehicles for one-tap booking.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {vehicles.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border px-3 py-2" data-testid={`vehicle-row-${v.vehicle_number}`}>
              <div className="flex items-center gap-2 text-sm">{React.createElement(VT_META[v.vehicle_type]?.icon || Car, { className: "h-4 w-4 text-muted-foreground" })} <span className="font-mono font-medium">{v.vehicle_number}</span>{v.nickname && <span className="text-xs text-muted-foreground">{v.nickname}</span>}</div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(v)} aria-label="Remove vehicle" data-testid={`vehicle-remove-${v.vehicle_number}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {vehicles.length === 0 && <p className="text-sm text-muted-foreground">No saved vehicles yet.</p>}
        </div>
        <div className="rounded-lg border p-3 space-y-2">
          <div className="grid grid-cols-3 gap-1.5">{VT_KEYS.map((k) => <button key={k} type="button" onClick={() => setVt(k)} data-testid={`vehicle-add-vt-${k}`} className={`rounded-md border px-2 py-2 text-xs min-h-[40px] inline-flex items-center justify-center gap-1 ${vt === k ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-accent"}`}>{React.createElement(VT_META[k].icon, { className: "h-3.5 w-3.5" })} {VT_META[k].short}</button>)}</div>
          <Input value={vn} onChange={(e) => setVn(e.target.value.toUpperCase())} placeholder="Vehicle number" className="h-10 font-mono" data-testid="vehicle-add-number" />
          <Input value={nick} onChange={(e) => setNick(e.target.value)} placeholder="Nickname (optional) — e.g. Office car" className="h-10" data-testid="vehicle-add-nickname" />
          <Button className="w-full h-10" onClick={add} disabled={busy || vn.replace(/[^A-Z0-9]/g, "").length < 4} data-testid="vehicle-add-save"><Plus className="h-4 w-4 mr-1" /> Save vehicle</Button>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="vehicles-close">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
